"use client";

import { useRef, useMemo } from "react";
import Link from "next/link";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PrevNextNav, { getProjectNav } from "@/components/PrevNextNav";

function FloatingGeometry() {
  const groupRef = useRef<THREE.Group>(null);
  const mesh1Ref = useRef<THREE.Mesh>(null);
  const mesh2Ref = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) {
      groupRef.current.rotation.y = t * 0.01;
    }
    if (mesh1Ref.current) {
      mesh1Ref.current.rotation.x = t * 0.05;
      mesh1Ref.current.position.y = Math.sin(t * 0.2) * 0.3;
    }
    if (mesh2Ref.current) {
      mesh2Ref.current.rotation.y = t * 0.04;
      mesh2Ref.current.position.y = Math.sin(t * 0.25 + 1) * 0.25;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh ref={mesh1Ref} position={[3, 0.5, -2]}>
        <octahedronGeometry args={[1.5, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.2} transparent />
      </mesh>
      <mesh ref={mesh2Ref} position={[-2.5, -0.5, -1]}>
        <icosahedronGeometry args={[1.2, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.15} transparent />
      </mesh>
    </group>
  );
}

function DotGrid() {
  const pointsRef = useRef<THREE.Points>(null);
  const positions = useMemo(() => {
    const pos = [];
    const gridSize = 20;
    const spacing = 0.8;
    for (let x = -gridSize / 2; x < gridSize / 2; x++) {
      for (let y = -gridSize / 2; y < gridSize / 2; y++) {
        pos.push(x * spacing, y * spacing, -5);
      }
    }
    return new Float32Array(pos);
  }, []);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.z = state.clock.elapsedTime * 0.005;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial color="#1a1a1a" size={0.015} opacity={0.08} transparent />
    </points>
  );
}

function Scene() {
  return (
    <>
      <FloatingGeometry />
      <DotGrid />
    </>
  );
}

const project = {
  title: "TruScore",
  subtitle: "Data services, full-stack development, and AI enablement for a leadership development company",
  heroImage: "/images/work/truscore-card.svg",
  challenge:
    "TruScore helps organizations measure and grow their leaders through 360-degree feedback, coaching, and development tools. Always looking ahead, TruScore set out to bring AI into how it builds and operates, and wanted the right platform and the right workflows to make it count across product, operations, and client delivery.",
  approach:
    "Mapleton Hill helped TruScore choose Claude, then collaborated with each of its teams to put it to work efficiently, from building and enhancing the platform with Claude Code to everyday work in Claude, Claude Cowork, and Claude Design. Together we shaped practical, team-specific workflows so Claude became part of how TruScore builds, not a side experiment. Alongside that work, Mapleton Hill supports TruScore with data services and full-stack development.",
  services: ["Claude Training & Enablement", "AI-Assisted Development", "Data Services", "Full-Stack Development"],
  results: [
    { value: "75%", label: "Of TruScore team members using Claude" },
    { value: "50%", label: "Faster delivery of platform enhancements" },
  ],
};

const clientQuote = {
  text: "Mapleton Hill helped us choose Claude and then worked alongside each of our teams to build it into how we actually work, from developing our platform to our day-to-day operations. It has changed how quickly we can deliver for our clients.",
  author: "Tom Kuhne",
  role: "Managing Partner and CTO, TruScore",
};

const glance = [
  {
    label: "Client",
    value: (
      <a href="https://www.truscore.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff6900] transition-colors underline decoration-[#ff6900]/40 underline-offset-4">
        TruScore
      </a>
    ),
    icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
  },
  {
    label: "Industry",
    value: "Leadership Development & Assessment",
    icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
  },
  {
    label: "Services",
    value: "Claude Training & Enablement, AI-Assisted Development, Data Services, Full-Stack Development",
    icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
  },
  {
    label: "Built With",
    value: "Claude, Claude Code, Claude Cowork, and Claude Design by Anthropic",
    icon: "M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4",
  },
  {
    label: "Deployment",
    value: "In production across TruScore's teams",
    icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  },
];

export default function TruScorePage() {
  return (
    <div className="min-h-screen bg-[#fafafa] text-[#1a1a1a] relative overflow-hidden">
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Scene />
        </Canvas>
      </div>

      <div className="relative z-10">
        <Navigation />

        <main id="main-content">

        {/* Hero */}
        <section className="pt-28 pb-8 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-4">
              <Link
                href="/work"
                className="inline-flex items-center gap-2 text-sm text-[#1a1a1a]/70 hover:text-[#ff6900] transition-colors mb-4"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Work
              </Link>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.1] tracking-tight mb-4">
                {project.title}
              </h1>
              <p className="text-xl text-[#ff6900] font-medium">
                {project.subtitle}
              </p>
            </div>
          </div>
        </section>

        {/* Hero Media */}
        <section className="px-6 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl aspect-[16/10] md:aspect-[19/10] bg-[#1a1a1a]">
              <img
                src={project.heroImage}
                alt="TruScore logo"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* At a Glance */}
        <section className="py-16 px-6 bg-white border-y border-[#1a1a1a]/5">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-10 text-center">At a Glance</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
              {glance.map((item) => (
                <div key={item.label} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#ff6900]/10 flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#ff6900]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                    </svg>
                  </div>
                  <p className="text-sm text-[#1a1a1a]/70 uppercase tracking-wider mb-2">{item.label}</p>
                  <p className="text-lg font-semibold">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Opportunity */}
        <section className="py-24 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-6">The Opportunity</h2>
            <p className="text-2xl md:text-3xl text-[#1a1a1a]/80 leading-relaxed font-light">
              {project.challenge}
            </p>
          </div>
        </section>

        {/* The Approach */}
        <section className="py-24 px-6 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-6">The Approach</h2>
            <p className="text-2xl md:text-3xl text-white/80 leading-relaxed font-light mb-12">
              {project.approach}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {project.services.map((service) => (
                <span key={service} className="px-4 py-2 bg-white/10 rounded-full text-white/70">
                  {service}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="py-16 md:py-24 px-6 bg-white/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-12 text-center">Results</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 text-center">
              {project.results.map((stat) => (
                <div key={stat.label}>
                  <div className="text-6xl md:text-7xl font-bold text-[#ff6900] mb-4">{stat.value}</div>
                  <p className="text-lg text-[#1a1a1a]/70 leading-relaxed">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Client quote */}
        <section className="py-20 px-6 bg-[#1a1a1a]">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-8">Client Perspective</p>
            <svg className="w-12 h-12 text-[#ff6900] mx-auto mb-8 opacity-50" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
            </svg>
            <blockquote className="text-2xl md:text-3xl text-white font-light leading-relaxed mb-8">
              {clientQuote.text}
            </blockquote>
            <footer className="text-white/50">
              <div className="text-[#ff6900] font-medium text-lg">{clientQuote.author}</div>
              <div className="mt-1 max-w-2xl mx-auto">{clientQuote.role}</div>
            </footer>
          </div>
        </section>

        <PrevNextNav
          {...getProjectNav("/work/truscore")}
          basePath="/work"
          baseLabel="All Work"
        />

        {/* CTA */}
        <section className="py-24 px-6 bg-gradient-to-r from-[#ff6900] to-[#c2410c] text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
              Ready to start your project?
            </h2>
            <p className="text-xl text-white/70 mb-10">
              Let&apos;s discuss how we can help bring your ideas to life.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-[#1a1a1a] font-medium hover:bg-white/90 transition-colors"
            >
              Get in touch
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </section>

        </main>

        <Footer />
      </div>
    </div>
  );
}
